import { useSpinDelay } from 'spin-delay'
import type { VariantProps } from '@joyful/utils'
import { tv } from '@joyful/utils'
import type { ReactNode } from 'react'
import { z } from 'zod'
import { Alert, AlertActions, AlertContent, AlertDescription } from '../Alert'
import type { SpinningLoaderProps } from '../SpinningLoader'
import { SpinningLoader } from '../SpinningLoader'

const errorStateVariants = tv({
  base: 'w-full max-w-full',

  variants: {
    height: {
      fill: 'flex min-h-52 grow basis-full items-center',
    },
  },
})

export interface ErrorStateProps<E = Error> extends VariantProps<
  typeof errorStateVariants
> {
  children?: ReactNode
  error?: E
}

function getErrorStateMessage<E>(error: E | undefined) {
  if (error instanceof Error) {
    return error.message
  }

  const parsed = z.string().safeParse(error)
  if (parsed.success) {
    return parsed.data
  }

  return 'An error occurred.'
}

export function ErrorState<E = Error>({
  children,
  error,
  height,
}: ErrorStateProps<E>) {
  const message = getErrorStateMessage(error)

  return (
    <div className={errorStateVariants({ height })}>
      <Alert variant="error">
        <AlertContent>
          <AlertDescription>{message}</AlertDescription>
        </AlertContent>
        {children ? <AlertActions>{children}</AlertActions> : null}
      </Alert>
    </div>
  )
}

export interface SpinDelayProps {
  readonly children: ReactNode

  /**
   * @default 200
   */
  readonly delayMs?: number

  /**
   * @default 100
   */
  readonly minDurationMs?: number
}

function SpinDelay({
  children,
  delayMs = 200,
  minDurationMs = 100,
}: SpinDelayProps) {
  const shouldShow = useSpinDelay(true, {
    delay: delayMs,
    minDuration: minDurationMs,
    ssr: false,
  })

  if (shouldShow || delayMs === 0) {
    return children
  }

  return null
}

export interface DelayedSpinnerProps
  extends
    Omit<SpinDelayProps, 'children'>,
    Pick<SpinningLoaderProps, 'height' | 'size'> {}

export function DelayedSpinner({
  height,
  size,
  ...props
}: DelayedSpinnerProps) {
  return (
    <SpinDelay {...props}>
      <SpinningLoader height={height} size={size} />
    </SpinDelay>
  )
}

export interface RawQueryStateProps {
  readonly children?: ReactNode
  readonly error?: ReactNode
  readonly isError: boolean
  readonly isPending: boolean
  readonly isSuccess: boolean
  readonly loading?: ReactNode
  readonly loadingOptions?: Omit<SpinDelayProps, 'children'>
}

const defaultError = <ErrorState />

export function RawQueryState({
  children,
  error = defaultError,
  isError,
  isPending,
  isSuccess,
  loading,
  loadingOptions,
}: RawQueryStateProps) {
  if (isPending) {
    return loading ?? <DelayedSpinner {...loadingOptions} />
  }

  if (isError) {
    return error
  }

  if (isSuccess) {
    return children
  }

  return null
}

export interface QueryStateQueryLike<E = Error> {
  readonly error: E
  readonly isError: boolean
  readonly isPending: boolean
  readonly isSuccess: boolean
}

export interface QueryStateProps<E = Error> extends Pick<
  RawQueryStateProps,
  'loading' | 'loadingOptions'
> {
  children?: ReactNode
  error?: (error: E) => ReactNode
  /**
   * @default query.isPending
   */
  isPending?: boolean

  query: QueryStateQueryLike<E>
}

function renderQueryError<E>(error: E) {
  if (error instanceof Error) {
    return <ErrorState error={error} />
  }

  return <ErrorState />
}

export function QueryState<E = Error>({
  children,
  error = renderQueryError,
  isPending,
  loading,
  loadingOptions,
  query,
}: QueryStateProps<E>) {
  const resolvedIsPending = isPending ?? query.isPending
  const resolvedError = query.isError ? error(query.error) : undefined

  return (
    <RawQueryState
      isPending={resolvedIsPending}
      isError={query.isError}
      isSuccess={query.isSuccess}
      loading={loading}
      loadingOptions={loadingOptions}
      error={resolvedError}
    >
      {children}
    </RawQueryState>
  )
}

export interface QueriesStateProps<E = Error> extends Omit<
  QueryStateProps<E>,
  'query'
> {
  queries: Array<QueryStateQueryLike<E>>
}

export function QueriesState<E = Error>({
  children,
  error = renderQueryError,
  loading,
  loadingOptions,
  queries,
}: QueriesStateProps<E>) {
  const failedQuery = queries.find((query) => {
    return query.isError
  })
  const isError = Boolean(failedQuery)
  const resolvedError = failedQuery ? error(failedQuery.error) : undefined

  return (
    <RawQueryState
      isPending={queries.some((query) => {
        return query.isPending
      })}
      isError={isError}
      isSuccess={queries.every((query) => {
        return query.isSuccess
      })}
      loading={loading}
      loadingOptions={loadingOptions}
      error={resolvedError}
    >
      {children}
    </RawQueryState>
  )
}
